import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import CampoEditavel from "./CampoEditavel";
import PassosDoItem from "./PassosDoItem";
import palette from "../../common/styles/palette";
import { avaliarDeteccao, CORES, ROTULOS } from "./detectabilidade";
import { InterviewDetection, InterviewItem, InterviewStep } from "../../types/Interview";

interface Props {
  item: InterviewItem;
  numero?: number;
  busy: boolean;
  onEditarItem: (
    item: InterviewItem,
    campo: "question" | "value",
    texto: string
  ) => void;
  onAlternarCritico: (item: InterviewItem) => void;
  onRemover: (item: InterviewItem) => void;
  onEditarFaceta: (
    item: InterviewItem,
    campo: keyof InterviewDetection,
    texto: string
  ) => void;
  /**
   * Os critérios de aceite (ADR-0003). OPCIONAL de propósito: sem este handler o
   * bloco de passos não aparece, e a tela segue idêntica ao que era antes —
   * mesma razão pela qual o backend omite `steps` quando o Item não os tem.
   * Recebe a lista inteira porque a op `set_steps` substitui tudo.
   */
  onEditarPassos?: (item: InterviewItem, passos: InterviewStep[]) => void;
  onPedirAoAssistente: (item: InterviewItem) => void;
  sugestaoStatus?: "carregando" | "pronta" | null;
  /** Reutiliza o mesmo cartão para as evidências visuais complementares. */
  camera2?: boolean;
}

export default function RegrasDoItem({
  item,
  numero,
  busy,
  onEditarItem,
  onAlternarCritico,
  onRemover,
  onEditarFaceta,
  onEditarPassos,
  onPedirAoAssistente,
  sugestaoStatus = null,
  camera2 = false,
}: Props) {
  const { t } = useTranslation();
  const [parciais, setParciais] = useState<Partial<InterviewDetection>>({});

  const det = avaliarDeteccao({
    ...item,
    detection: { ...item.detection, ...parciais },
  }, camera2);
  const semRegraComplementar = camera2 && !Object.values(item.detection).some((valor) =>
    typeof valor === "string" ? valor.trim() !== "" : Array.isArray(valor) && valor.length > 0
  );

  const renderIconeStatus = () => {
    if (det.nivel === "forte")
      return (
        <CheckCircleIcon fontSize="small" sx={{ color: palette.emeraldDeep }} />
      );
    if (det.nivel === "revisar")
      return (
        <WarningAmberIcon fontSize="small" sx={{ color: palette.warning }} />
      );
    return (
      <ErrorOutlineIcon fontSize="small" sx={{ color: palette.dangerText }} />
    );
  };

  const handleCommit = (campo: keyof InterviewDetection, texto: string) => {
    setParciais((p) => {
      const { [campo]: _, ...resto } = p;
      return resto;
    });
    onEditarFaceta(item, campo, texto);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2.5,
        borderRadius: 2.5,
        border: `1px solid ${palette.border}`,
        bgcolor: "#ffffff",
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderWidth: 1,
        },
        transition: "box-shadow 0.2s ease-in-out",
        "&:hover": {
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
        },
      }}
    >
      {/* O Item também é editável aqui: a regra perde o sentido se o professor
          precisa voltar de aba para corrigir o enunciado, o peso ou a
          criticidade que ela está explicando. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: "1 1 320px", minWidth: 240 }}>
          <CampoEditavel
            valor={item.question}
            label={numero !== undefined ? `Item ${numero}` : "Item avaliado"}
            multiline
            minRows={1}
            fullWidth
            disabled={busy}
            onComitar={(texto) => onEditarItem(item, "question", texto)}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Chip
            size="small"
            icon={semRegraComplementar ? undefined : renderIconeStatus()}
            label={semRegraComplementar ? t("interview_camera2_no_rule") : ROTULOS[det.nivel]}
            color={semRegraComplementar ? "default" : CORES[det.nivel]}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Tooltip title={t("interview_toggle_critical")}>
            <Chip
              size="small"
              color={item.critical ? "error" : "default"}
              variant={item.critical ? "filled" : "outlined"}
              label={
                item.critical
                  ? t("interview_eliminatory")
                  : t("interview_critical")
              }
              disabled={busy}
              onClick={() => onAlternarCritico(item)}
              sx={{ cursor: busy ? "default" : "pointer", fontWeight: 600 }}
            />
          </Tooltip>
          <CampoEditavel
            valor={item.value === null ? "" : String(item.value)}
            label="Peso"
            numero
            largura={76}
            disabled={busy}
            onComitar={(texto) => onEditarItem(item, "value", texto)}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={busy && sugestaoStatus === null}
            startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
            onClick={() => onPedirAoAssistente(item)}
            sx={{
              borderColor: palette.brandSoft,
              color: palette.brand,
              bgcolor: palette.brandSoft + "40",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { bgcolor: palette.brandSoft },
            }}
          >
            {sugestaoStatus === "carregando"
              ? "ANÁLISE EM ANDAMENTO"
              : sugestaoStatus === "pronta"
              ? "Ver regras sugeridas"
              : t("interview_ask_assistant")}
          </Button>
          <Button
            size="small"
            color="error"
            disabled={busy}
            aria-label={`${t("interview_remove")} ${item.question}`}
            onClick={() => onRemover(item)}
            sx={{ minWidth: 0, px: 1, textTransform: "none" }}
          >
            {t("interview_remove")}
          </Button>
        </Box>
      </Box>

      {/* Alerta de qualidade da regra (se não for forte) */}
      {!semRegraComplementar && det.nivel !== "forte" && (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            mb: 2,
            borderRadius: 1.5,
            bgcolor:
              det.nivel === "revisar"
                ? palette.warningSoft
                : palette.dangerSoft,
            borderLeft: `4px solid ${
              det.nivel === "revisar" ? palette.warning : palette.dangerText
            }`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: palette.text, fontWeight: 500 }}
          >
            {det.motivo}
          </Typography>
        </Box>
      )}

      {/* Grid estruturado de critérios de percepção e decisão */}
      <Grid container spacing={2}>
        {/* Coluna 1: O que ouvir e o que ver (Percepção) */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: palette.background,
              border: `1px solid ${palette.border}`,
              height: "100%",
            }}
          >
            {!camera2 && <>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: palette.brandDark,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <HearingOutlinedIcon
                fontSize="small"
                sx={{ color: palette.brand }}
              />
              Canal de Áudio e Fala
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: palette.textMuted,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Falas ou palavras-chave esperadas do aluno:
              </Typography>
              <CampoEditavel
                valor={item.detection?.speech || ""}
                placeholder="Ex: Pergunta sobre dor, irradiação, tempo de início, pergunta se já teve antes..."
                multiline
                minRows={2}
                fullWidth
                disabled={busy}
                onDigitar={(parcial) =>
                  setParciais((p) => ({ ...p, speech: parcial }))
                }
                onComitar={(texto) => handleCommit("speech", texto)}
              />
            </Box>

            </>}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: palette.brandDark,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <VideocamOutlinedIcon
                fontSize="small"
                sx={{ color: palette.brand }}
              />
              {camera2 ? "Canal Visual e Evidências Observáveis" : "Canal Visual e Ações Físicas"}
            </Typography>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: palette.textMuted,
                  display: "block",
                  mb: 0.5,
                }}
              >
                {camera2 ? "Evidências visuais relacionadas ao critério do item, conforme o cenário e o checklist:" : "Ações corporais ou procedimentos observáveis no vídeo:"}
              </Typography>
              <CampoEditavel
                valor={item.detection?.video || ""}
                label={camera2 ? String(t("interview_camera2_video")) : undefined}
                placeholder={camera2 ? String(t("interview_camera2_video_placeholder")) : "Ex: Inspeciona membro, palpa pulsos distais, checa tempo de enchimento capilar..."}
                multiline
                minRows={2}
                fullWidth
                disabled={busy}
                onDigitar={(parcial) =>
                  setParciais((p) => ({ ...p, video: parcial }))
                }
                onComitar={(texto) => handleCommit("video", texto)}
              />
            </Box>
          </Box>
        </Grid>

        {/* Coluna 2: Regras de Decisão da IA (Aprovação / Reprovação / Notas) */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: palette.background,
              border: `1px solid ${palette.border}`,
              height: "100%",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: palette.brandDark,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <CheckCircleOutlineIcon
                fontSize="small"
                sx={{ color: palette.emeraldDeep }}
              />
              Regra de Aprovação do Item
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: palette.textMuted,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Aprovar quando (fato observável concreto):
              </Typography>
              <CampoEditavel
                valor={item.detection?.correct_when || ""}
                label={camera2 ? String(t("interview_camera2_correct_when")) : undefined}
                placeholder={camera2 ? String(t("interview_camera2_correct_when_placeholder")) : "Ex: O aluno verbaliza a investigação do trauma e realiza o exame físico do membro afetado."}
                multiline
                minRows={2}
                fullWidth
                disabled={busy}
                onDigitar={(parcial) =>
                  setParciais((p) => ({ ...p, correct_when: parcial }))
                }
                onComitar={(texto) => handleCommit("correct_when", texto)}
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: palette.textMuted,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <HighlightOffOutlinedIcon
                  fontSize="small"
                  sx={{ color: palette.dangerText, fontSize: 16 }}
                />
                Não realizou quando:
              </Typography>
              <CampoEditavel
                valor={item.detection?.not_done_when || ""}
                label={camera2 ? String(t("interview_camera2_not_done_when")) : undefined}
                placeholder={camera2 ? String(t("interview_camera2_not_done_when_placeholder")) : "Ex: Não pergunta sobre o mecanismo da lesão ou não avalia o membro comprometido."}
                multiline
                minRows={1}
                fullWidth
                disabled={busy}
                onDigitar={(parcial) =>
                  setParciais((p) => ({ ...p, not_done_when: parcial }))
                }
                onComitar={(texto) => handleCommit("not_done_when", texto)}
              />
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: palette.textMuted,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ color: palette.brand, fontSize: 16 }}
                />
                Não penalizar por (tolerâncias/exceções):
              </Typography>
              <CampoEditavel
                valor={item.detection?.note || ""}
                label={camera2 ? String(t("interview_camera2_note")) : undefined}
                placeholder={camera2 ? String(t("interview_camera2_note_placeholder")) : "Ex: Não penalizar se a ordem das perguntas for invertida."}
                multiline
                minRows={1}
                fullWidth
                disabled={busy}
                onDigitar={(parcial) =>
                  setParciais((p) => ({ ...p, note: parcial }))
                }
                onComitar={(texto) => handleCommit("note", texto)}
              />
            </Box>

            {onEditarPassos && (
              <PassosDoItem
                cameraFocal={camera2}
                passos={item.detection?.steps ?? []}
                busy={busy}
                onComitar={(passos) => onEditarPassos(item, passos)}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
